import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { OpButton } from 'new-components/DLS/OpButton/OpButton';
import { OpSpace } from 'new-components/DLS/OpSpace/OpSpace';
import { notificationService } from 'containers/AlertWrapper/notificationService';
import { useSelectorJs } from 'utils/customHooks';
import { useConfirmModal } from 'utils/customHooks/useConfirmModal';
import { useIdentityAndUser } from 'utils/customHooks/useIdentityAndUser';
import { useValidateScopes } from 'utils/customHooks/useValidateScopes';
import { getOpParams } from 'utils/getOpParams';
import { heliumSend } from 'utils/helpers';
import { RequestLicensesModal } from 'new-components/RequestLicenses/RequestLicensesModal';

import { visitorSmallPackageId } from '../../../../../constants/packageIds';
import { AppDispatch, ImmutableRootState } from '../../../../../store/store';
import { searchVisitorsToday } from '../../../../../store/slices/visitorSearchSlice';
import {
  fetchVisits,
  VisitsState,
} from '../../../../../store/slices/visitsSlice';
import { LocationsState } from '../../../../../store/slices/locationsSlice';
import { useVisitorFeature } from '../../../../../hooks/useVisitorFeature';

import ProfileEditModal from './ProfileEditModal';

interface ProfileDrawerHeaderProps {
  onClose: () => void;
}

const ProfileDrawerHeader = ({ onClose }: ProfileDrawerHeaderProps) => {
  const { t } = useTranslation();

  const dispatch: AppDispatch = useDispatch();

  const { orgId } = getOpParams();
  const { userId: globalUserId } = useIdentityAndUser();
  const { isValid: hasAllvisitorsWrite } = useValidateScopes([
    'o{orgId}-visitorAllVisitors:w',
    'o{orgId}-visitorVisitors:w',
    'o:w',
    's-o:w',
  ]);
  const { isValid: hasInviteWrite } = useValidateScopes([
    'o{orgId}-visitorInvite:w',
    'o{orgId}-visitorVisitors:w',
    'o:w',
    's-o:w',
  ]);
  const { isValid: hasSigninWrite } = useValidateScopes([
    'o{orgId}-visitorSignIn:w',
    'o{orgId}-visitorVisitors:w',
    'o:w',
    's-o:w',
  ]);
  const { isValid: hasSignoutWrite } = useValidateScopes([
    'o{orgId}-visitorSignOut:w',
    'o{orgId}-visitorVisitors:w',
    'o:w',
    's-o:w',
  ]);

  const selectedVisit = useSelectorJs((state: ImmutableRootState) =>
    state.getIn(['visits', 'selectedVisit']),
  ) as VisitsState['selectedVisit'];
  const selectedVisitor = useSelectorJs((state: ImmutableRootState) =>
    state.getIn(['visits', 'selectedVisitor']),
  ) as VisitsState['selectedVisitor'];
  const locations = useSelectorJs((state: ImmutableRootState) =>
    state.getIn(['locations', 'locations']),
  ) as LocationsState['locations'];

  const locationCount = Array.isArray(locations) ? locations.length : 0;

  const { totalLicenses } = useVisitorFeature(orgId);

  const licensesLeft = totalLicenses - locationCount;

  const [isProfileEditModalOpen, setIsProfileEditModalOpen] = useState(false);
  const [isRequestLicensesModalOpen, setIsRequestLicensesModalOpen] =
    useState(false);

  const confirmModal = useConfirmModal();

  const checkLicenseAndProceed = (callback: () => void) => {
    if (locationCount > totalLicenses) {
      confirmModal({
        title: t('License limit reached'),
        content: `You have ${Math.max(0, licensesLeft)} licenses remaining.`,
        okText: t('Request licenses'),
        onOk: () => setIsRequestLicensesModalOpen(true),
      });
      return;
    }

    callback();
  };

  const onDeleteClick = () => {
    confirmModal({
      title: t('visitors.profile.confirmDelete.title'),
      content: t('visitors.profile.confirmDelete.message'),
      okText: t('visitors.profile.confirmDelete.ok'),
      cancelText: t('visitors.profile.confirmDelete.cancel'),
      onOk: async () => {
        try {
          if (selectedVisit && selectedVisit.visitors.length > 1) {
            await heliumSend(
              'removeVisitors',
              [orgId, selectedVisit.id, selectedVisitor?.id],
              {},
            );
            notificationService.show(
              'success',
              t('visitors.profile.notification.deleteSuccess'),
            );
            dispatch(fetchVisits({ orgId }));
            dispatch(searchVisitorsToday({ orgId }));
            onClose();
          } else {
            notificationService.show(
              'error',
              t('visitors.profile.notification.deleteError'),
            );
          }
        } catch {
          // Failed to delete visitor
        }
      },
    });
  };

  return (
    <>
      <OpSpace>
        {((selectedVisitor?.status === 1 && hasInviteWrite) ||
          (selectedVisitor?.status === 2 && hasSigninWrite) ||
          (selectedVisitor?.status === 3 && hasSignoutWrite)) &&
          (hasAllvisitorsWrite ||
            globalUserId === selectedVisit?.host.userId) && (
            <>
              {selectedVisitor.status === 1 && (
                <OpButton
                  type="primary"
                  danger
                  ghost
                  onClick={() => checkLicenseAndProceed(onDeleteClick)}
                >
                  {t('visitors.profile.header.delete')}
                </OpButton>
              )}
              <OpButton
                type="primary"
                ghost
                onClick={() =>
                  checkLicenseAndProceed(() => setIsProfileEditModalOpen(true))
                }
              >
                {t('visitors.profile.header.edit')}
              </OpButton>
            </>
          )}
      </OpSpace>
      {isProfileEditModalOpen && (
        <ProfileEditModal
          open={isProfileEditModalOpen}
          onClose={() => setIsProfileEditModalOpen(false)}
        />
      )}
      <RequestLicensesModal
        orgId={orgId}
        featureName={t('Visitor')}
        modalOpen={isRequestLicensesModalOpen}
        onClose={() => setIsRequestLicensesModalOpen(false)}
        packageId={visitorSmallPackageId}
      />
    </>
  );
};

export default ProfileDrawerHeader;
